import { useMemo } from "react"

import SuperChart, { type StackedBarChartData } from "~/components/reusable/graphs/SuperChart"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { ChartType, LegendAlignment } from "~/constants/graphs"
import type { InsightScalars } from "~/utils/demandInsightValues"
import { formatNumber } from "~/utils/formatting"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { formatName } from "~/utils/namingUtils"
import { getTailwindValue } from "~/utils/tailwind"

/** Summed over one set of variants: the whole route, or one node. */
export type DiscountFigures = {
  /** Realised REORDERS, pre-orders excluded: the population the rate is measured on. */
  comparableSales: number
  /** Discount-weighted units: `discountedSales / comparableSales` is the average depth. */
  discountedSales: number
  /** What removing the discounts takes out, ≤ 0. */
  impactUnits: number
}

/** The same fold the row's units come from, so the chart and the switch share one population. */
export const sumDiscountFigures = (
  variants: readonly IVariantSummary[],
  scalarsFor: (variantId: string) => InsightScalars | null,
): DiscountFigures => {
  const out: DiscountFigures = { comparableSales: 0, discountedSales: 0, impactUnits: 0 }
  for (const v of variants) {
    const s = scalarsFor(v.variantId)
    if (!s) continue
    out.comparableSales += s.comparableSales ?? 0
    out.discountedSales += s.discountedSales ?? 0
    out.impactUnits += s.discountImpactUnits ?? 0
  }
  return out
}

/**
 * As legacy `MarkdownGraph.tsx` drew it: comparable reorders split into full-price and
 * discount-added, summing to what sold. One column: the insight is an overall figure. Pre-orders
 * are excluded: their discounts are booked with the order (legacy's `quantity − preOrderQuantity`).
 */
const DiscountsBreakdown = ({ figures }: { figures: DiscountFigures }) => {
  const { comparableSales, discountedSales, impactUnits } = figures
  // NOOS measures on exactly what the baseline reads (own sales or matches), not on comparables.
  const ownSales = useDemand2Scope().kind === "noos"
  // `impactUnits` is floored at −comparableSales, so the full-price segment never goes negative.
  const withoutDiscount = Math.max(comparableSales + impactUnits, 0)
  const addedByDiscount = Math.max(-impactUnits, 0)
  const averageDepth = comparableSales > 0 ? discountedSales / comparableSales : null

  const sales = formatName("sale", true, true)
  const data = useMemo(
    (): StackedBarChartData[] => [
      {
        name: `Discounted ${formatName("sale", true)}`,
        type: "column",
        color: getTailwindValue("--color-purpleheart-500"),
        data: [Math.round(addedByDiscount)],
      },
      {
        name: `${sales} without discount`,
        type: "column",
        color: getTailwindValue("--color-violet-200"),
        data: [Math.round(withoutDiscount)],
      },
    ],
    [addedByDiscount, withoutDiscount, sales],
  )

  if (comparableSales <= 0) return null

  return (
    <div className="flex flex-col gap-3">
      <SuperChart
        type={ChartType.StackedColumn}
        data={data}
        xCategories={[ownSales ? "Baseline reorders" : "Comparable reorders"]}
        height={240}
        legendAlignment={LegendAlignment.Bottom}
      />
      <p className="max-w-[52ch] paragraph-sm text-fg-subtle">
        {`Measured on ${ownSales ? "the reorders in the weeks the baseline reads" : "the comparable variants' reorders"}, so pre-orders are excluded. Discounts added ${formatNumber(Math.round(addedByDiscount))} of the ${formatNumber(Math.round(comparableSales))} units they sold${averageDepth == null ? "" : `, at ${formatNumber(averageDepth * 100, 1)}% off on average`}.`}
      </p>
    </div>
  )
}

export default DiscountsBreakdown
